/**
 * Los tres estados que tiene toda vista que carga datos, resueltos una vez.
 *
 * INVENTARIO_VISTAS.md §1.3 los fija: carga con `Skeleton` que replica la
 * forma real (`UI-10`, nunca un spinner genérico), error de red con `Callout
 * tone="critical"` y botón de reintentar, y el contenido cuando hay datos.
 *
 * Existe para que las 17 vistas no repitan ese `if (isLoading) … if (error) …`
 * quince veces, cada una con una redacción distinta del mismo mensaje.
 *
 * `children` es una función y no un nodo a propósito: así los datos llegan ya
 * definidos y la vista no tiene que volver a comprobar `undefined` — el
 * compilador lo garantiza.
 */
import type { ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Skeleton } from '@mpfn/ui'
import { Button } from '@mpfn/ui'
import { Callout } from '@mpfn/patterns'
import { ErrorApi } from '../api/cliente.js'

/** Silueta por defecto: una cabecera y tres filas. Se sustituye con `silueta`. */
function SiluetaPorDefecto() {
  return (
    <div className="sigfisc-silueta">
      <Skeleton className="sigfisc-silueta__titulo" />
      <Skeleton className="sigfisc-silueta__fila" />
      <Skeleton className="sigfisc-silueta__fila" />
      <Skeleton className="sigfisc-silueta__fila" />
    </div>
  )
}

interface PropsDatos<T> {
  estado: UseQueryResult<T>
  /** Silueta propia de la vista, para que la carga tenga su forma real. */
  silueta?: ReactNode
  children: (datos: T) => ReactNode
}

export function Datos<T>({ estado, silueta, children }: PropsDatos<T>) {
  if (estado.isPending) return <>{silueta ?? <SiluetaPorDefecto />}</>

  if (estado.isError) {
    const error = estado.error
    const esPermiso = error instanceof ErrorApi && error.esPermiso

    // Un 403 puntual no es un fallo del sistema: es información que esta
    // persona no puede ver. Se dice sin revelar QUÉ se ocultó (§1.3).
    return (
      <Callout
        tone={esPermiso ? 'warning' : 'critical'}
        title={esPermiso ? 'No tiene acceso a esta información' : 'No se pudieron cargar los datos'}
        {...(esPermiso
          ? {}
          : {
              action: (
                <Button variant="secondary" size="sm" onClick={() => void estado.refetch()}>
                  Reintentar
                </Button>
              ),
            })}
      >
        {error instanceof ErrorApi
          ? error.message
          : 'No fue posible comunicarse con el servidor. Vuelva a intentarlo.'}
      </Callout>
    )
  }

  return <>{children(estado.data)}</>
}
